import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart, Printer, Share2, Loader2, ChevronRight } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/RegionContext';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import ShareButtons from '@/components/ShareButtons';
import WishlistButton from '@/components/WishlistButton';
import ProgressiveImage from '@/components/ProgressiveImage';
import ReviewSection from '@/components/ReviewSection';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArtworkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [similar, setSimilar] = useState([]);

  useEffect(() => {
    const fetchArtwork = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/artworks/${id}`);
        setArtwork(res.data);
        const simRes = await axios.get(`${API}/artworks/${id}/similar?limit=6`);
        setSimilar(simRes.data || []);
      } catch {
        toast.error('Artwork not found');
        navigate('/gallery');
      } finally {
        setLoading(false);
      }
    };
    fetchArtwork();
  }, [id, navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="artwork-loading">
      <Loader2 className="animate-spin text-accent" size={32} />
    </div>
  );

  if (!artwork) return null;

  const imageUrl = artwork.image_url?.startsWith('/') ? `${window.location.origin}${artwork.image_url}` : artwork.image_url;
  const pageUrl = `${window.location.origin}/artwork/${artwork.id}`;
  const altText = `${artwork.title} — ${artwork.medium || 'painting'} by Gangadhar Som${artwork.dimensions ? `, ${artwork.dimensions}` : ''}${artwork.year ? ` (${artwork.year})` : ''}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "VisualArtwork",
    "name": artwork.title,
    "description": artwork.description || `Original ${artwork.medium || 'painting'} by Gangadhar Som`,
    "url": pageUrl,
    "image": imageUrl,
    "artist": { "@type": "Person", "name": "Gangadhar Som", "@id": `${window.location.origin}/#artist` },
    "artMedium": artwork.medium || "",
    "artform": "Painting",
    "genre": artwork.category || "",
    "keywords": (artwork.tags || []).join(', '),
    ...(artwork.year ? { "dateCreated": artwork.year } : {}),
    ...(artwork.dimensions ? { "size": artwork.dimensions } : {}),
    ...(artwork.price ? {
      "offers": {
        "@type": "Offer",
        "price": artwork.price,
        "priceCurrency": "INR",
        "availability": artwork.for_sale !== false ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
        "seller": { "@type": "Person", "name": "Gangadhar Som" }
      }
    } : {}),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
      { "@type": "ListItem", "position": 2, "name": "Gallery", "item": `${window.location.origin}/gallery` },
      { "@type": "ListItem", "position": 3, "name": artwork.title, "item": pageUrl },
    ]
  };

  return (
    <div className="min-h-screen pt-20 sm:pt-28 pb-16 sm:pb-24" data-testid="artwork-detail-page">
      <SEO
        title={`${artwork.title} by Gangadhar Som | Original ${artwork.medium || 'Painting'}`}
        description={artwork.description || `"${artwork.title}" — original ${artwork.medium || 'painting'}${artwork.dimensions ? `, ${artwork.dimensions}` : ''}${artwork.year ? ` (${artwork.year})` : ''} by renowned Indian artist Gangadhar Som. Buy original art and prints.`}
        keywords={[artwork.title, artwork.medium, artwork.category, ...(artwork.tags || []), 'Gangadhar Som', 'buy painting online', 'Indian art'].filter(Boolean).join(', ')}
        url={pageUrl}
        type="article"
        image={imageUrl}
        price={artwork.for_sale !== false ? artwork.price : null}
        availability={artwork.for_sale !== false ? 'in stock' : 'sold out'}
        schema={schema}
      />

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-4 sm:mb-6" data-testid="breadcrumb">
          <ol className="flex items-center gap-1 text-xs font-sans text-muted-foreground" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" itemProp="item" className="hover:text-accent transition-colors"><span itemProp="name">Home</span></Link>
              <meta itemProp="position" content="1" />
            </li>
            <ChevronRight size={12} />
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/gallery" itemProp="item" className="hover:text-accent transition-colors"><span itemProp="name">Gallery</span></Link>
              <meta itemProp="position" content="2" />
            </li>
            <ChevronRight size={12} />
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span itemProp="name" className="text-foreground font-medium truncate max-w-[200px] inline-block">{artwork.title}</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        {/* Main content */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="relative">
            <div className="bg-muted rounded-lg overflow-hidden">
              <ProgressiveImage
                src={artwork.image_url}
                placeholder={artwork.placeholder}
                alt={altText}
                className="w-full h-auto max-h-[75vh] object-contain"
                data-testid="artwork-main-image"
              />
            </div>
          </motion.div>

          {/* Details */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col">
            <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight mb-2" data-testid="artwork-title">
              {artwork.title}
            </h1>

            <p className="font-sans text-sm text-accent uppercase tracking-[0.15em] mb-4" data-testid="artwork-artist">
              Gangadhar Som
            </p>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground font-sans mb-4">
              {artwork.medium && <span data-testid="artwork-medium">{artwork.medium}</span>}
              {artwork.dimensions && <span data-testid="artwork-dimensions">{artwork.dimensions}</span>}
              {artwork.year && <span data-testid="artwork-year">{artwork.year}</span>}
              {artwork.category && <span className="capitalize" data-testid="artwork-category">{artwork.category}</span>}
            </div>

            {artwork.description && (
              <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-6" data-testid="artwork-description">
                {artwork.description}
              </p>
            )}

            {/* Price & Actions */}
            {artwork.for_sale !== false && artwork.price ? (
              <div className="space-y-3 mb-6">
                <p className="font-serif text-2xl text-accent" data-testid="artwork-price">{formatPrice(artwork.price)}</p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => { addToCart({ ...artwork, type: 'original' }); toast.success('Added to cart'); }}
                    className="bg-accent hover:bg-accent/90 flex-1"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart size={16} className="mr-2" /> Add to Cart
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/print/${artwork.id}`)}
                    data-testid="order-print-btn"
                  >
                    <Printer size={16} className="mr-2" /> Order Print
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <p className="font-sans text-sm text-muted-foreground bg-muted/50 rounded-lg px-4 py-3">
                  {artwork.for_sale === false ? 'This original has been sold.' : 'Contact for pricing.'}
                  {' '}Prints and digital downloads may still be available.
                </p>
                <Button variant="outline" className="mt-3" onClick={() => navigate(`/print/${artwork.id}`)}>
                  <Printer size={16} className="mr-2" /> Order Print
                </Button>
              </div>
            )}

            {/* Tags */}
            {artwork.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-6" data-testid="artwork-tags">
                {artwork.tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/gallery?search=${encodeURIComponent(tag)}`}
                    className="px-2.5 py-1 bg-muted rounded-full text-xs font-sans text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            )}

            {/* Share & Wishlist */}
            <div className="flex items-start gap-3 pt-4 border-t border-border">
              <div className="flex-1"><ShareButtons artwork={artwork} /></div>
              <WishlistButton artworkId={artwork.id} />
            </div>

            {/* Reviews */}
            <ReviewSection artworkId={artwork.id} artworkTitle={artwork.title} />
          </motion.div>
        </div>

        {/* Similar Artworks */}
        {similar.length > 0 && (
          <section className="mt-16" data-testid="similar-artworks">
            <h2 className="font-serif text-xl sm:text-2xl font-light mb-6">You May Also Like</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {similar.map(s => (
                <Link
                  key={s.id}
                  to={`/artwork/${s.id}`}
                  className="group relative overflow-hidden rounded-lg aspect-square bg-muted"
                  data-testid={`similar-${s.id}`}
                >
                  <ProgressiveImage
                    src={s.thumb_url || s.image_url}
                    placeholder={s.placeholder}
                    alt={`${s.title} by Gangadhar Som — ${s.medium || 'painting'}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                    <span className="text-white text-xs font-sans truncate">{s.title}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Additional structured data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      </div>
    </div>
  );
};

export default ArtworkDetail;
